import { UserProfile, BusinessRuleResult, PlanLimitations, PlanFeature } from '../types';
import { adminService } from '@/core/services/AdminService';
import { PLAN_FEATURES, getPlanFeatures, isUnlimited } from '@/constants/plans';

export class PlanRules {
  /**
   * Legacy planFeatures structure for backward compatibility
   * Maps new PLAN_FEATURES to old PlanFeature[] format
   */
  private static get planFeatures(): Record<string, PlanFeature[]> {
    const legacyFeatures: Record<string, PlanFeature[]> = {};
    
    for (const [planType, features] of Object.entries(PLAN_FEATURES)) {
      legacyFeatures[planType] = [
        {
          feature: 'profile_views',
          enabled: true,
          limit: features.profile_views_daily > 0 ? features.profile_views_daily : undefined,
          description: isUnlimited(features.profile_views_daily) 
            ? 'Visualizações ilimitadas' 
            : `Máximo ${features.profile_views_daily} visualizações por dia`
        },
        {
          feature: 'connections',
          enabled: true,
          limit: features.likes_daily > 0 ? features.likes_daily : undefined,
          description: isUnlimited(features.likes_daily) 
            ? 'Conexões ilimitadas' 
            : `Máximo ${features.likes_daily} conexões por dia`
        },
        {
          feature: 'chat',
          enabled: true,
          limit: features.new_conversations_daily > 0 ? features.new_conversations_daily : undefined,
          description: isUnlimited(features.new_conversations_daily) 
            ? 'Mensagens ilimitadas' 
            : `Máximo ${features.new_conversations_daily} novas conversas por dia`
        },
        {
          feature: 'advanced_filters',
          enabled: features.advanced_filters,
          description: features.advanced_filters 
            ? 'Filtros avançados disponíveis' 
            : 'Filtros avançados não disponíveis'
        },
        {
          feature: 'verified_filter',
          enabled: features.verified_filter,
          description: features.verified_filter 
            ? 'Filtro de perfis verificados disponível' 
            : 'Filtro de perfis verificados não disponível'
        },
        {
          feature: 'profile_boost',
          enabled: features.highlight_in_feed,
          description: features.highlight_in_feed 
            ? 'Destaque no feed' 
            : 'Sem destaque no feed'
        },
        {
          feature: 'priority_support',
          enabled: planType.includes('premium'),
          description: planType.includes('premium') 
            ? 'Suporte prioritário 24/7' 
            : 'Suporte prioritário não disponível'
        },
        {
          feature: 'read_receipts',
          enabled: !planType.includes('free'),
          description: !planType.includes('free') 
            ? 'Confirmação de leitura' 
            : 'Confirmação de leitura não disponível'
        }
      ];
    }
    
    return legacyFeatures;
  }

  static async canAccessFeature(user: UserProfile, feature: string): Promise<BusinessRuleResult> {
    // Admins bypassa todas as restrições
    const isAdmin = await adminService.isAdmin(user.id);
    if (isAdmin) {
      return {
        allowed: true,
        reason: 'Administrador tem acesso total',
        metadata: { adminBypass: true }
      };
    }

    const planFeatures = this.planFeatures[user.plan_type] || this.planFeatures.sponsor_free;
    const featureConfig = planFeatures.find(f => f.feature === feature);

    if (!featureConfig) {
      return {
        allowed: false,
        reason: `Funcionalidade '${feature}' não configurada para o plano ${user.plan_type}`
      };
    }

    if (!featureConfig.enabled) {
      return {
        allowed: false,
        reason: featureConfig.description || `Funcionalidade '${feature}' não disponível no plano ${user.plan_type}`
      };
    }

    return {
      allowed: true,
      reason: featureConfig.description || `Funcionalidade '${feature}' disponível`,
      data: { limit: featureConfig.limit },
      metadata: { planType: user.plan_type }
    };
  }

  static getPlanLimitations(planType: string): PlanLimitations {
    const features = this.planFeatures[planType] || this.planFeatures.sponsor_free;
    
    const dailyLimits: Record<string, number> = {};
    const monthlyLimits: Record<string, number> = {};

    features.forEach(feature => {
      if (feature.limit) {
        // Algumas limitações são diárias, outras mensais
        if (['chat', 'profile_views'].includes(feature.feature)) {
          dailyLimits[feature.feature] = feature.limit;
        } else {
          monthlyLimits[feature.feature] = feature.limit;
        }
      }
    });

    return {
      features,
      dailyLimits,
      monthlyLimits
    };
  }

  static isPlanExpired(user: UserProfile): boolean {
    if (!user.current_plan_expires_at) return false;
    
    const expirationDate = new Date(user.current_plan_expires_at);
    const now = new Date();
    
    return expirationDate < now;
  }

  static getEffectivePlan(user: UserProfile): string {
    // Se o plano expirou, retorna para sponsor_free
    if (this.isPlanExpired(user)) {
      return 'sponsor_free';
    }
    
    return user.plan_type;
  }

  /**
   * Get profile priority days for a plan
   * Returns minimum age in days that a profile must have to be visible
   * 0 = immediate access, 1 = 24h+, 3 = 3 days+
   */
  static getProfilePriorityDays(planType: string): number {
    const features = getPlanFeatures(planType);
    return features.profile_priority_days;
  }

  /**
   * Check if user can use "verified only" filter
   */
  static canUseVerifiedFilter(planType: string, userRole?: string): boolean {
    const features = getPlanFeatures(planType);
    return features.verified_filter;
  }

  /**
   * Get all daily limits for a plan
   */
  static getDailyLimits(planType: string): {
    profileViews: number;
    likes: number;
    newConversations: number;
  } {
    const features = getPlanFeatures(planType);
    return {
      profileViews: features.profile_views_daily,
      likes: features.likes_daily,
      newConversations: features.new_conversations_daily,
    };
  }

  /**
   * Check if a specific limit is unlimited
   */
  static isLimitUnlimited(limit: number): boolean {
    return isUnlimited(limit);
  }

  /**
   * Get boosts included in plan per billing period
   */
  static getBoostsIncluded(planType: string, billingPeriod: 'monthly' | 'quarterly'): number {
    const features = getPlanFeatures(planType);
    return features.boosts_included[billingPeriod] || 0;
  }

  /**
   * Check if user can buy additional boosts
   */
  static canBuyBoosts(planType: string): boolean {
    const features = getPlanFeatures(planType);
    return features.can_buy_boosts;
  }

  /**
   * Check if profile should be highlighted in feed
   */
  static hasHighlightInFeed(planType: string): boolean {
    const features = getPlanFeatures(planType);
    return features.highlight_in_feed;
  }

  /**
   * Check if user has access to advanced filters
   */
  static hasAdvancedFilters(planType: string): boolean {
    const features = getPlanFeatures(planType);
    return features.advanced_filters;
  }
}
